import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { AppState, AppStateStatus, Platform } from 'react-native';
import SmartUpdateService from '../services/smartUpdateService';

// Safe import of IAUUpdateStatus - use shim on web
let IAUUpdateStatus: any;
if (Platform.OS === 'web') {
  // Web doesn't support in-app updates
  IAUUpdateStatus = {
    UNKNOWN: 0, NOT_AVAILABLE: 1, AVAILABLE: 2, DOWNLOADING: 3, 
    INSTALLING: 4, INSTALLED: 5, FAILED: 6, CANCELED: 7, 
    DOWNLOADED: 8, PENDING: 9, CHECKING: 10, IDLE: 11 
  };
} else {
  try {
    const inAppUpdatesModule = require('react-native-in-app-updates');
    IAUUpdateStatus = inAppUpdatesModule.IAUUpdateStatus || {
      UNKNOWN: 0, NOT_AVAILABLE: 1, AVAILABLE: 2, DOWNLOADING: 3, 
      INSTALLING: 4, INSTALLED: 5, FAILED: 6, CANCELED: 7, 
      DOWNLOADED: 8, PENDING: 9, CHECKING: 10, IDLE: 11 
    };
  } catch (error) {
    console.warn('react-native-in-app-updates not available in UpdateProvider:', error);
    IAUUpdateStatus = {
      UNKNOWN: 0, NOT_AVAILABLE: 1, AVAILABLE: 2, DOWNLOADING: 3, 
      INSTALLING: 4, INSTALLED: 5, FAILED: 6, CANCELED: 7, 
      DOWNLOADED: 8, PENDING: 9, CHECKING: 10, IDLE: 11 
    };
  }
}

interface UpdateContextType {
  updateAvailable: boolean;
  updateStatus: number;
  isChecking: boolean;
  isUpdating: boolean;
  checkForUpdate: () => Promise<void>;
  startUpdate: (type: 'flexible' | 'immediate') => Promise<void>;
  installUpdate: () => Promise<void>;
  dismissUpdate: () => void;
  showUpdateModal: boolean;
  setShowUpdateModal: (show: boolean) => void;
  updateError: string | null;
  clearError: () => void;
  storeVersion?: string;
  currentVersion?: string;
}

const UpdateContext = createContext<UpdateContextType | undefined>(undefined);

interface UpdateProviderProps {
  children: ReactNode;
  autoCheck?: boolean;
  checkInterval?: number; // in hours
}

export const UpdateProvider: React.FC<UpdateProviderProps> = ({
  children,
  autoCheck = true,
  checkInterval = 24
}) => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [updateStatus, setUpdateStatus] = useState<number>(IAUUpdateStatus.IDLE);
  const [isChecking, setIsChecking] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [storeVersion, setStoreVersion] = useState<string | undefined>();
  const [currentVersion, setCurrentVersion] = useState<string | undefined>();

  // Check for updates
  const checkForUpdate = async () => {
    // Skip on web
    if (Platform.OS === 'web') {
      return;
    }

    if (isChecking) return;
    
    setIsChecking(true);
    setUpdateError(null);
    
    try {
      const shouldCheck = await SmartUpdateService.shouldCheckForUpdate();
      if (!shouldCheck) {
        console.log('Skipping update check - too soon since last check');
        return;
      }

      const result = await SmartUpdateService.checkForUpdate();
      
      if (result.available) {
        setUpdateAvailable(true);
        setShowUpdateModal(true);
        setStoreVersion(result.storeVersion);
        setCurrentVersion(result.currentVersion);
        await SmartUpdateService.updateLastCheckTime();
      } else {
        setUpdateAvailable(false);
        console.log('No update available:', result.reason);
      }
    } catch (error) {
      console.error('Error checking for updates:', error);
      setUpdateError((error as Error).message || 'Failed to check for updates');
    } finally {
      setIsChecking(false);
    }
  };

  // Start update process
  const startUpdate = async (type: 'flexible' | 'immediate') => {
    if (isUpdating) return;
    
    setIsUpdating(true);
    setUpdateError(null);
    
    try {
      // For SmartUpdateService, we just open Play Store
      SmartUpdateService.openPlayStore();
      setUpdateAvailable(false);
      setShowUpdateModal(false);
    } catch (error) {
      console.error('Error starting update:', error);
      setUpdateError((error as Error).message || 'Failed to start update');
    } finally {
      setIsUpdating(false);
    }
  };

  // Install update (for flexible updates)
  const installUpdate = async () => {
    try {
      // For SmartUpdateService, we just open Play Store
      SmartUpdateService.openPlayStore();
      setUpdateAvailable(false);
      setShowUpdateModal(false);
    } catch (error) {
      console.error('Error installing update:', error);
      setUpdateError((error as Error).message || 'Failed to install update');
    }
  };

  // Dismiss update notification
  const dismissUpdate = () => {
    setShowUpdateModal(false);
    setUpdateAvailable(false);
  };

  // Clear error
  const clearError = () => {
    setUpdateError(null);
  };

  // Handle app state changes
  useEffect(() => {
    // Skip AppState listener on web
    if (Platform.OS === 'web') {
      return;
    }

    const handleAppStateChange = (nextAppState: AppStateStatus) => {
      if (nextAppState === 'active' && autoCheck) {
        // Check for updates when app becomes active
        checkForUpdate();
      }
    };

    const subscription = AppState.addEventListener('change', handleAppStateChange);
    
    return () => {
      subscription?.remove();
    };
  }, [autoCheck]);

  // SmartUpdateService doesn't need status listeners
  // All update handling is done through the checkForUpdate method

  // Initial check for updates
  useEffect(() => {
    if (autoCheck) {
      // Delay initial check to avoid blocking app startup
      const timer = setTimeout(() => {
        checkForUpdate();
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [autoCheck]);

  // SmartUpdateService doesn't need cleanup

  const value: UpdateContextType = {
    updateAvailable,
    updateStatus,
    isChecking,
    isUpdating,
    checkForUpdate,
    startUpdate,
    installUpdate,
    dismissUpdate,
    showUpdateModal,
    setShowUpdateModal,
    updateError,
    clearError,
    storeVersion,
    currentVersion
  };

  return (
    <UpdateContext.Provider value={value}>
      {children}
    </UpdateContext.Provider>
  );
};

// Custom hook to use update context
export const useUpdate = (): UpdateContextType => {
  const context = useContext(UpdateContext);
  if (context === undefined) {
    throw new Error('useUpdate must be used within an UpdateProvider');
  }
  return context;
};

export default UpdateProvider;
